import { useState, useEffect, useMemo } from "react";
import "./App.css";
import { BACKEND_URL, apiHeaders } from "./config";

export default function PairingPage() {
  return <PairingPageContent />;
}

// ── AI Auto-párování ─────────────────────────────────────────────────────────

function AutoPairingModal({ selectedCategory, onClose, onConfirmAll }) {
  const [stav, setStav] = useState("idle"); // idle | loading | hotovo | chyba
  const [navrhy, setNavrhy] = useState([]);
  const [potvrzene, setPotvrzene] = useState(new Set());
  const [zprava, setZprava] = useState("");
  const [ukladam, setUkladam] = useState(false);

  const spustAI = async () => {
    setStav("loading");
    setNavrhy([]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/parovani/auto`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({ kategorie: selectedCategory }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba serveru");
      setNavrhy(data.navrhy || []);
      setPotvrzene(new Set(data.navrhy?.map((_, i) => i) || [])); // vše zaškrtnuto defaultně
      setZprava(data.zprava || "");
      setStav("hotovo");
    } catch (e) {
      setZprava(e.message);
      setStav("chyba");
    }
  };

  const ulozPotvrzene = async () => {
    setUkladam(true);
    const navrhyKUlozeni = navrhy.filter((_, i) => potvrzene.has(i));
    for (const navrhItem of navrhyKUlozeni) {
      // První ID = boppe_id (hledáme Ploty Boppe, jinak první)
      const boppe = navrhItem.detaily.find(p => p.eshop === "Ploty Boppe") || navrhItem.detaily[0];
      const linked = navrhItem.detaily.filter(p => p.id !== boppe.id);
      await fetch(`${BACKEND_URL}/api/parovani`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({
          boppe_id: boppe.id,
          linked_ids: linked.map(p => p.id),
        }),
      });
    }
    setUkladam(false);
    onConfirmAll(navrhyKUlozeni);
    onClose();
  };

  const toggleNavrh = (i) => {
    setPotvrzene(prev => {
      const n = new Set(prev);
      if (n.has(i)) { n.delete(i); } else { n.add(i); }
      return n;
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box auto-pairing-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🤖 AI Auto-párování</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {stav === "idle" && (
          <div className="modal-body">
            <p>Claude projde všechny <strong>nespárované produkty</strong>{selectedCategory !== "Vše" ? ` v kategorii „${selectedCategory}"` : ""} a navrhne shody mezi e-shopy.</p>
            <p className="ai-note">⚡ Obvykle trvá 10–30 sekund.</p>
            <button className="btn-primary" onClick={spustAI}>🚀 Spustit analýzu</button>
          </div>
        )}

        {stav === "loading" && (
          <div className="modal-body ai-loading">
            <div className="ai-spinner" />
            <p>Claude analyzuje produkty…</p>
          </div>
        )}

        {stav === "chyba" && (
          <div className="modal-body">
            <p className="ai-error">❌ {zprava}</p>
            <button className="btn-secondary" onClick={spustAI}>Zkusit znovu</button>
          </div>
        )}

        {stav === "hotovo" && navrhy.length === 0 && (
          <div className="modal-body">
            <p>✅ {zprava || "Claude nenašel žádné nové shody."}</p>
          </div>
        )}

        {stav === "hotovo" && navrhy.length > 0 && (
          <>
            <div className="modal-body">
              <p className="ai-summary">
                Claude navrhl <strong>{navrhy.length} párování</strong>.
                Odškrtněte co nechcete uložit, pak klikněte Uložit.
              </p>
              <div className="navrhy-list">
                {navrhy.map((n, i) => (
                  <div key={i} className={`navrh-item ${potvrzene.has(i) ? "navrh-ok" : "navrh-skip"}`}>
                    <div className="navrh-header">
                      <input
                        type="checkbox"
                        className="navrh-checkbox"
                        checked={potvrzene.has(i)}
                        onChange={() => toggleNavrh(i)}
                      />
                      <span className="navrh-jistota">{n.jistota}%</span>
                      <span className="navrh-duvod">📌 {n.duvod}</span>
                    </div>
                    <div className="navrh-produkty">
                      {n.detaily.map(p => (
                        <span key={p.id} className={`navrh-tag${p.eshop === "Ploty Boppe" ? " boppe-tag" : ""}`}>
                          <strong>{p.eshop}</strong>: {p.nazev} {p.varianta ? `(${p.varianta}mm)` : ""}
                          {p.cena ? ` — ${p.cena} Kč` : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Zrušit</button>
              <button className="btn-primary" onClick={ulozPotvrzene} disabled={ukladam || potvrzene.size === 0}>
                {ukladam ? "Ukládám…" : `✅ Uložit ${potvrzene.size} párování`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── AI Rozšíření párování ────────────────────────────────────────────────────

const ROZSIRITELNE_ESHOPY = ["MM Ploty", "E-Pletivo", "Plot Eshop"];

function ExtendPairingModal({ selectedCategory, onClose, onConfirmAll }) {
  const [stav, setStav] = useState("idle"); // idle | loading | hotovo | chyba
  const [navrhy, setNavrhy] = useState([]);
  const [potvrzene, setPotvrzene] = useState(new Set());
  const [zprava, setZprava] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [vybrane, setVybrane] = useState(new Set(ROZSIRITELNE_ESHOPY));

  const toggleEshop = (e) => setVybrane(prev => {
    const n = new Set(prev);
    n.has(e) ? n.delete(e) : n.add(e);
    return n;
  });

  const spustAI = async () => {
    setStav("loading");
    setNavrhy([]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/parovani/rozsir`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({
          kategorie: selectedCategory,
          eshopy_k_doplneni: Array.from(vybrane),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba serveru");
      setNavrhy(data.navrhy || []);
      setPotvrzene(new Set((data.navrhy || []).map((_, i) => i)));
      setZprava(data.zprava || "");
      setStav("hotovo");
    } catch (e) {
      setZprava(e.message);
      setStav("chyba");
    }
  };

  const ulozPotvrzene = async () => {
    setUkladam(true);
    const navrhyKUlozeni = navrhy.filter((_, i) => potvrzene.has(i));
    for (const n of navrhyKUlozeni) {
      // Načti aktuální linked_ids pro tuto skupinu a přidej nový produkt
      const res = await fetch(`${BACKEND_URL}/api/parovani`, { headers: apiHeaders() });
      const vsechna = await res.json();
      const stavajici = vsechna[n.skupina_id] || [];
      await fetch(`${BACKEND_URL}/api/parovani`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify({
          boppe_id: n.skupina_id,
          linked_ids: [...stavajici, n.nove_id],
        }),
      });
    }
    setUkladam(false);
    onConfirmAll(navrhyKUlozeni);
    onClose();
  };

  const toggleNavrh = (i) => setPotvrzene(prev => {
    const n = new Set(prev);
    n.has(i) ? n.delete(i) : n.add(i);
    return n;
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box auto-pairing-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Rozšířit párování o nové e-shopy</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {stav === "idle" && (
          <div className="modal-body">
            <p>Claude projde <strong>existující párování</strong>{selectedCategory !== "Vše" ? ` v kategorii „${selectedCategory}"` : ""} a ke každé skupině zkusí najít odpovídající produkt z nových e-shopů.</p>
            <div className="eshop-checkboxes">
              {ROZSIRITELNE_ESHOPY.map(e => (
                <label key={e} className="eshop-checkbox-label">
                  <input type="checkbox" checked={vybrane.has(e)} onChange={() => toggleEshop(e)} />
                  {e}
                </label>
              ))}
            </div>
            <p className="ai-note">⚡ Obvykle trvá 15–40 sekund (závisí na počtu skupin).</p>
            <button className="btn-primary" onClick={spustAI} disabled={vybrane.size === 0}>
              🚀 Spustit rozšíření
            </button>
          </div>
        )}

        {stav === "loading" && (
          <div className="modal-body ai-loading">
            <div className="ai-spinner" />
            <p>Claude hledá shody v nových e-shopech…</p>
          </div>
        )}

        {stav === "chyba" && (
          <div className="modal-body">
            <p className="ai-error">❌ {zprava}</p>
            <button className="btn-secondary" onClick={() => setStav("idle")}>Zpět</button>
          </div>
        )}

        {stav === "hotovo" && navrhy.length === 0 && (
          <div className="modal-body">
            <p>✅ {zprava || "Claude nenašel žádné nové shody."}</p>
          </div>
        )}

        {stav === "hotovo" && navrhy.length > 0 && (
          <>
            <div className="modal-body">
              <p className="ai-summary">
                Claude navrhl <strong>{navrhy.length} rozšíření</strong>.
                Odškrtněte co nechcete uložit.
              </p>
              <div className="navrhy-list">
                {navrhy.map((n, i) => (
                  <div key={i} className={`navrh-item ${potvrzene.has(i) ? "navrh-ok" : "navrh-skip"}`}>
                    <div className="navrh-header">
                      <input type="checkbox" className="navrh-checkbox" checked={potvrzene.has(i)} onChange={() => toggleNavrh(i)} />
                      <span className="navrh-jistota">{n.jistota}%</span>
                      <span className="navrh-duvod">📌 {n.duvod}</span>
                    </div>
                    <div className="navrh-produkty">
                      {n.existujici.map(p => (
                        <span key={p.id} className={`navrh-tag${p.eshop?.includes("Boppe") ? " boppe-tag" : ""}`}>
                          <strong>{p.eshop}</strong>: {p.nazev}
                        </span>
                      ))}
                      <span className="navrh-tag navrh-tag-new">
                        ➕ <strong>{n.novy.eshop}</strong>: {n.novy.nazev}{n.novy.varianta ? ` (${n.novy.varianta}mm)` : ""}
                        {n.novy.cena ? ` — ${n.novy.cena} Kč` : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Zrušit</button>
              <button className="btn-primary" onClick={ulozPotvrzene} disabled={ukladam || potvrzene.size === 0}>
                {ukladam ? "Ukládám…" : `✅ Uložit ${potvrzene.size} rozšíření`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PairingPageContent() {
  const [products, setProducts] = useState([]);
  const [pairings, setPairings] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Vše");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingPairingId, setEditingPairingId] = useState(null);
  const [nacteno, setNacteno] = useState({ produkty: false, parovani: false });
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);

  // Set všech spárovaných ID (klíče i hodnoty) — přepočítá se jen při změně párování
  const sparovianeIds = useMemo(() => {
    const ids = new Set();
    for (const [boppeId, linkedIds] of Object.entries(pairings)) {
      ids.add(boppeId);
      linkedIds.forEach(id => ids.add(id));
    }
    return ids;
  }, [pairings]);

  const categories = [
    { name: "Vše", subcategories: [] },
    {
      name: "Pletivový plot",
      subcategories: ["Pletivo bez nd 15m", "Pletivo bez nd 25m", "Pletivo s nd 15m", "Pletivo s nd 25m",
        "Plotové sloupky 38mm", "Plotové sloupky 48mm", "Plotové vzpěry", "Podhrabové desky"],
    },
    {
      name: "Betonový plot",
      subcategories: ["Betonové desky 1str", "Betonové desky 2str",
        "Betonové sloupky prubezne sede", "Betonové sloupky koncove sede", "Betonové sloupky rohove sede",
        "Betonové sloupky prubezne piskovec", "Betonové sloupky koncove piskovec", "Betonové sloupky rohove piskovec"],
    },
    {
      name: "Panelový systém",
      subcategories: ["Panelový systém 3D 4mm", "Panelový systém 3D 5mm", "Panelový systém 2D", "Sloupky pro panely"],
    },
    {
      name: "Brány a branky",
      subcategories: ["Pletivové branky celovýplet", "Pletivové branky psaníčko",
        "Pletivové brány psaníčko", "Pletivové brány celovýplet", "Panelové branky", "Panelové brány"],
    },
    {
      name: "Příslušenství",
      subcategories: ["Příslušenství pro pletivo", "Příslušenství pro podhrab", "Příslušenství pro panely"],
    },
  ];

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/produkty`, { headers: apiHeaders() })
      .then(r => r.json())
      .then(data => {
        const updated = data.map(p => {
          let cena = parseFloat(p.cena);
          if (p.eshop === "Pletiva Dobry" || p.eshop === "Pilecký") {
            if (p.kategorie?.includes("25m")) cena = cena * 25;
            else if (p.kategorie?.includes("15m")) cena = cena * 15;
          }
          return { ...p, cena: isNaN(cena) ? p.cena : cena.toFixed(2) };
        });
        setProducts(updated);
        setNacteno(p => ({ ...p, produkty: true }));
      })
      .catch(e => console.error("❌ Produkty:", e));

    fetch(`${BACKEND_URL}/api/parovani`, { headers: apiHeaders() })
      .then(r => r.json())
      .then(data => { setPairings(data); setNacteno(p => ({ ...p, parovani: true })); })
      .catch(e => console.error("❌ Párování:", e));
  }, []);

  const toggleSelectProduct = (product) => {
    if (selectedProducts.length > 0) {
      const scrapers = new Set(selectedProducts.map(p => p.eshop));
      if (scrapers.has(product.eshop)) {
        alert("❌ Nelze párovat produkty ze stejného e-shopu!");
        return;
      }
    }
    setSelectedProducts(prev =>
      prev.some(p => p.id === product.id)
        ? prev.filter(p => p.id !== product.id)
        : [...prev, product]
    );
  };

  const removeSelectedProduct = (id) => setSelectedProducts(prev => prev.filter(p => p.id !== id));

  const handleEditPairing = (pairingId) => {
    setIsEditing(true);
    setEditingPairingId(pairingId);
    const linked = pairings[pairingId] || [];
    const selected = [products.find(p => p.id === pairingId), ...linked.map(id => products.find(p => p.id === id))];
    setSelectedProducts(selected.filter(Boolean));
  };

  const handleConfirmEdit = () => {
    if (selectedProducts.length < 2) { alert("Musíte vybrat alespoň 2 produkty!"); return; }
    const boppe_id = selectedProducts[0].id;
    const linked_ids = selectedProducts.slice(1).map(p => p.id);

    fetch(`${BACKEND_URL}/api/parovani`, {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({ boppe_id, linked_ids, old_boppe_id: editingPairingId }),
    })
      .then(r => r.json())
      .then(() => {
        const updated = { ...pairings };
        if (editingPairingId && editingPairingId !== boppe_id) delete updated[editingPairingId];
        updated[boppe_id] = linked_ids;
        setPairings(updated);
        setSelectedProducts([]);
        setIsEditing(false);
        setEditingPairingId(null);
      })
      .catch(e => console.error("❌ Úprava párování:", e));
  };

  const handlePairProducts = () => {
    if (selectedProducts.length < 2) { alert("Vyberte alespoň 2 produkty k párování!"); return; }
    const boppe_id = selectedProducts[0].id;
    const linked_ids = selectedProducts.slice(1).map(p => p.id);

    fetch(`${BACKEND_URL}/api/parovani`, {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({ boppe_id, linked_ids }),
    })
      .then(r => r.json())
      .then(() => {
        setPairings(prev => ({ ...prev, [boppe_id]: linked_ids }));
        setSelectedProducts([]);
      })
      .catch(e => console.error("❌ Uložení párování:", e));
  };

  const handleRemovePairing = (pairingId) => {
    fetch(`${BACKEND_URL}/api/parovani/${encodeURIComponent(pairingId)}`, {
      method: "DELETE",
      headers: apiHeaders(),
    })
      .then(() => {
        setPairings(prev => { const n = { ...prev }; delete n[pairingId]; return n; });
      })
      .catch(e => console.error("❌ Mazání párování:", e));
  };

  const toggleCategory = (name) => {
    setExpandedCategories(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="paring-container">
      <h1>⚙️ Nastavení — Párování produktů</h1>
      <div className="pairing-header">
        <p>Párováním produktů zajistíte, že odpovídající položky budou správně porovnávány mezi jednotlivými e-shopy.</p>
        <p className="warning-text">⚠️ Nesprávné párování může zkreslit cenová srovnání.</p>
        <div className="pairing-ai-buttons">
          <button className="btn-ai-pairing" onClick={() => setShowAutoModal(true)}>
            🤖 AI Auto-párování {selectedCategory !== "Vše" ? `— ${selectedCategory}` : "— vše"}
          </button>
          <button className="btn-ai-pairing btn-ai-extend" onClick={() => setShowExtendModal(true)}>
            🔗 Rozšířit o nové e-shopy
          </button>
        </div>
      </div>

      {showAutoModal && (
        <AutoPairingModal
          selectedCategory={selectedCategory}
          onClose={() => setShowAutoModal(false)}
          onConfirmAll={(ulozene) => {
            const updated = { ...pairings };
            for (const n of ulozene) {
              const boppe = n.detaily.find(p => p.eshop === "Ploty Boppe") || n.detaily[0];
              const linked = n.detaily.filter(p => p.id !== boppe.id);
              updated[boppe.id] = linked.map(p => p.id);
            }
            setPairings(updated);
          }}
        />
      )}

      {showExtendModal && (
        <ExtendPairingModal
          selectedCategory={selectedCategory}
          onClose={() => setShowExtendModal(false)}
          onConfirmAll={(ulozene) => {
            const updated = { ...pairings };
            for (const n of ulozene) {
              if (!updated[n.skupina_id]) updated[n.skupina_id] = [];
              if (!updated[n.skupina_id].includes(n.nove_id)) {
                updated[n.skupina_id] = [...updated[n.skupina_id], n.nove_id];
              }
            }
            setPairings(updated);
          }}
        />
      )}

      <aside className="sidebar">
        <ul>
          <li>
            <button className={`category-toggle ${selectedCategory === "Vše" ? "active" : ""}`}
              onClick={() => { setSelectedCategory("Vše"); setExpandedCategories({}); }}>
              Vše
            </button>
          </li>
          {categories.filter(c => c.name !== "Vše").map(category => (
            <li key={category.name}>
              <button className={`category-toggle ${selectedCategory === category.name ? "active" : ""}`}
                onClick={() => toggleCategory(category.name)}>
                <span className="category-icon">📂</span>
                <span className="category-name">{category.name}</span>
              </button>
              {expandedCategories[category.name] && (
                <ul className="subcategory-list">
                  {category.subcategories.map(sub => (
                    <li key={sub}>
                      <button className={selectedCategory === sub ? "active" : ""} onClick={() => setSelectedCategory(sub)}>
                        {sub}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </aside>

      <div className="product-list">
        {(!nacteno.produkty || !nacteno.parovani) && <p>⏳ Načítání produktů...</p>}
        {nacteno.produkty && nacteno.parovani && products
          .filter(p => selectedCategory === "Vše" || p.kategorie?.trim() === selectedCategory.trim())
          .filter(p => isEditing ? true : !sparovianeIds.has(p.id))
          .map(product => (
            <div key={product.id}
              className={`product-item ${selectedProducts.some(p => p.id === product.id) ? "selected" : ""}`}
              data-scraper={product.eshop}
              onClick={() => toggleSelectProduct(product)}>
              {selectedProducts.some(p => p.id === product.id) && (
                <button className="remove-selected" onClick={e => { e.stopPropagation(); removeSelectedProduct(product.id); }}>✖</button>
              )}
              <p><strong>{product.nazev}</strong> ({product.varianta ? `${product.varianta} mm` : "-"})</p>
              <p>{product.eshop}</p>
              <p>{product.cena} Kč</p>
            </div>
          ))}
      </div>

      <button onClick={handlePairProducts} className="pair-button">🔗 Spárovat vybrané produkty</button>

      <h2>📜 Spárované produkty</h2>
      <div className="paired-list">
        {Object.entries(pairings).length === 0 ? (
          <p>Žádná spárovaná data.</p>
        ) : (
          Object.entries(pairings)
            .filter(([mainId]) => {
              if (selectedCategory === "Vše") return true;
              const p = products.find(p => p.id === mainId);
              return p && p.kategorie === selectedCategory;
            })
            .map(([mainId, linkedIds]) => {
              const mainProduct = products.find(p => p.id === mainId);
              const linkedProducts = linkedIds.map(id => products.find(p => p.id === id)).filter(Boolean);
              if (!mainProduct) return null;
              return (
                <div key={mainId} className="paired-item">
                  <strong>{mainProduct.nazev}</strong> ({mainProduct.varianta ? `${mainProduct.varianta} mm` : "-"}) - {mainProduct.cena} Kč
                  <p><em>{mainProduct.eshop}</em></p>
                  <ul>
                    {linkedProducts.map(p => (
                      <li key={p.id}><strong>{p.nazev}</strong> ({p.varianta}) - {p.cena} Kč <em>{p.eshop}</em></li>
                    ))}
                  </ul>
                  <div className="button-group">
                    <button onClick={() => handleRemovePairing(mainId)} className="remove-button">❌ Odebrat párování</button>
                    <button onClick={() => isEditing ? setIsEditing(false) : handleEditPairing(mainId)} className="edit-button">
                      {isEditing && editingPairingId === mainId ? "❌ Zrušit úpravy" : "✏️ Upravit párování"}
                    </button>
                  </div>
                  {isEditing && editingPairingId === mainId && (
                    <div className="confirm-edit-container">
                      <button onClick={handleConfirmEdit} className="confirm-edit-button">✅ Potvrdit změnu</button>
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}
